import { consola } from 'consola'
import { watch } from 'vue'
import apiProfileInstance, { useUserProfileStore } from '@entities/user'

const logger = consola.withTag('marketing-consent')

/**
 * Согласие на рекламу, поставленное в форме регистрации.
 *
 * Отправить его сразу нельзя: половина сценариев регистрации уходит на подтверждение
 * телефона через Telegram или MAX, и токена в этот момент ещё нет — запрос к
 * `/users/consents` просто получил бы 401. Поэтому выбор ждёт здесь и уезжает
 * на сервер, как только появится профиль.
 *
 * Ключ снимается в любом исходе: повторная отправка при следующем входе означала бы
 * согласие, которого человек в тот раз не давал.
 */
const STORAGE_KEY = 'pending_marketing_consent'

/**
 * Сколько живёт отложенный выбор.
 *
 * Записывается он в момент нажатия способа входа, а доезжает после появления
 * профиля — между ними подтверждение телефона в Telegram/MAX, это минуты.
 * Без срока годности запись переживала бы недели: человек ткнул галочку, бросил
 * форму, а через день вошёл обычным способом — и получил бы согласие, которого
 * в тот раз не давал (или молчаливый отзыв, если галочку он перед уходом снял).
 */
const PENDING_TTL_MS = 60 * 60 * 1000

interface PendingConsent {
  granted: boolean
  ts: number
}

export const rememberMarketingConsent = (granted: boolean): void => {
  if (!process.client) return

  try {
    const value: PendingConsent = { granted, ts: Date.now() }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value))
  } catch (error) {
    logger.warn('Не удалось запомнить выбор по рекламе', error)
  }
}

const readPendingConsent = (): boolean | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw === null) return null

    // Прежний формат ('1'/'0') писался по каждому клику на галочку и возраста
    // не хранит — доверять ему нельзя, снимаем.
    if (raw === '1' || raw === '0') {
      clearPendingConsent()
      return null
    }

    const parsed = JSON.parse(raw) as Partial<PendingConsent>
    if (typeof parsed?.granted !== 'boolean' || typeof parsed?.ts !== 'number') {
      clearPendingConsent()
      return null
    }

    if (Date.now() - parsed.ts > PENDING_TTL_MS) {
      logger.debug('Отложенный выбор по рекламе протух — не отправляем')
      clearPendingConsent()
      return null
    }

    return parsed.granted
  } catch {
    return null
  }
}

const clearPendingConsent = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Приватный режим — переживём: без записи согласие просто не отправится.
  }
}

/**
 * Досылает отложенное согласие после появления профиля.
 * Вызывается один раз из плагина, как и инициализация пушей.
 */
export const usePendingMarketingConsent = (): void => {
  if (!process.client) return

  const userProfileStore = useUserProfileStore()

  watch(
    () => userProfileStore.user.id,
    async (newId, prevId) => {
      const isFirstLoad = (!prevId || prevId === 0) && newId && newId > 0
      if (!isFirstLoad) return

      const pending = readPendingConsent()
      if (pending === null) return

      try {
        await apiProfileInstance.updateConsent('marketing', pending)
        logger.debug(`Согласие на рекламу отправлено: ${pending}`)
      } catch (error) {
        logger.warn('Не удалось отправить согласие на рекламу', error)
      } finally {
        clearPendingConsent()
      }
    },
  )
}
